import React from 'react';
import { Loader2, AlertCircle, CheckCircle2, Info, Inbox, X } from 'lucide-react';
import { clsx } from 'clsx';

export const LoadingSpinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; text?: string }> = ({ size = 'md', text }) => {
  const sizes = { sm: 'w-4 h-4', md: 'w-6 h-6', lg: 'w-8 h-8' };
  return (
    <div className="flex flex-col items-center justify-center p-6 text-slate-400 gap-2">
      <Loader2 className={clsx('animate-spin text-sky-400', sizes[size])} />
      {text && <span className="text-xs font-medium">{text}</span>}
    </div>
  );
};

interface ToastProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({ message, type = 'info', onClose }) => {
  const icons = {
    success: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
    error: <AlertCircle className="w-5 h-5 text-rose-400" />,
    info: <Info className="w-5 h-5 text-sky-400" />,
  };

  const styles = {
    success: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
    error: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
    info: 'bg-sky-500/10 border-sky-500/20 text-sky-400',
  };

  return (
    <div className={clsx('flex items-center gap-3 px-4 py-3 border rounded-xl animate-in slide-in-from-bottom-2', styles[type])}>
      {icons[type]}
      <span className="text-sm font-medium">{message}</span>
      {onClose && (
        <button onClick={onClose} className="ml-2 text-slate-400 hover:text-slate-200" aria-label="Dismiss notification">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export const EmptyState: React.FC<{ message?: string; description?: string; icon?: React.ReactNode }> = ({
  message = 'No data found',
  description,
  icon,
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-slate-400">
      {icon || <Inbox className="w-10 h-10 mb-3 text-slate-600" />}
      <p className="text-sm font-medium text-slate-300">{message}</p>
      {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
    </div>
  );
};
